package com.sddamico.draftmachine.ds.core

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.gradientSteel
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span

/** Avatar diameters in px. */
enum class AvatarSize(val diameter: Int) { Xs(20), Sm(28), Md(38), Lg(52) }

/** First letters of the first two whitespace-separated words, uppercased; `?` when empty. */
private fun initials(name: String): String {
  val letters = name.trim()
    .split(Regex("\\s+"))
    .filter { it.isNotEmpty() }
    .take(2)
    .mapNotNull { it.firstOrNull() }
    .joinToString("")
    .uppercase()
  return letters.ifEmpty { "?" }
}

/**
 * A round steel-framed avatar — cover image when [src] is set, otherwise brass initials on a
 * steel gradient. Ports `components/core/Avatar.jsx`.
 *
 * @param ring copper ring when true, hairline strong border otherwise.
 */
@Composable
fun Avatar(
  name: String? = null,
  modifier: Modifier = Modifier,
  src: String? = null,
  size: AvatarSize = AvatarSize.Md,
  ring: Boolean = true,
) {
  val d = size.diameter
  val mod = Modifier
    .displayInlineFlex()
    .alignItems(AlignItems.Center)
    .justifyContent(JustifyContent.Center)
    .width(d.px)
    .height(d.px)
    .flex("0 0 auto")
    .borderRadius(50.percent)
    .then(if (src == null) Modifier.gradientSteel() else Modifier)
    .color(DmColor.brass300)
    .fontFamily(*DmType.heading)
    .fontWeight(DmType.weightBold)
    .fontSize((d * 0.38).px)
    .styleModifier { property("overflow", "hidden") }
    .then(
      if (ring) Modifier.border(2.px, LineStyle.Solid, DmColor.brass600)
      else Modifier.border(1.px, LineStyle.Solid, DmColor.borderStrong)
    )
    .boxShadowRaw("inset 0 1px 0 rgba(232,240,247,.12), 0 1px 3px rgba(0,0,0,.4)")
    .then(modifier)

  Span(attrs = mod.toAttrs()) {
    if (src != null) {
      Img(
        src = src,
        alt = name ?: "",
        attrs = Modifier
          .width(100.percent)
          .height(100.percent)
          .styleModifier { property("object-fit", "cover") }
          .toAttrs()
      )
    } else {
      SpanText(initials(name ?: ""))
    }
  }
}
